import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index";

const SearchPage = lazy(() => import("./pages/Search"));
const ProductPage = lazy(() => import("./pages/Product"));
const CategoryPage = lazy(() => import("./pages/Category"));
const BrandPage = lazy(() => import("./pages/Brand"));
const MissingCodePage = lazy(() => import("./pages/MissingCode"));
const DataQuality = lazy(() => import("./pages/DataQuality"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const Affiliate = lazy(() => import("./pages/Affiliate"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminMonetization = lazy(() => import("./pages/AdminMonetization"));
const WatIsEenIsdeMeldcode = lazy(() => import("./pages/content/WatIsEenIsdeMeldcode"));
const IsdeMeldcodeZoeken = lazy(() => import("./pages/content/IsdeMeldcodeZoeken"));
const WaarVindIkMijnMeldcode = lazy(() => import("./pages/content/WaarVindIkMijnMeldcode"));
const ProductNietInMeldcodelijst = lazy(() => import("./pages/content/ProductNietInMeldcodelijst"));
const IsdeSubsidieAanvragen = lazy(() => import("./pages/content/IsdeSubsidieAanvragen"));
const IsdeSubsidie2026 = lazy(() => import("./pages/content/IsdeSubsidie2026"));
const IsdeSubsidieBerekenen = lazy(() => import("./pages/content/IsdeSubsidieBerekenen"));
const IsdeMeldcodelijst2026 = lazy(() => import("./pages/content/IsdeMeldcodelijst2026"));
const IsdeWarmtepompSubsidie = lazy(() => import("./pages/content/IsdeWarmtepompSubsidie"));
const IsdeZonneboilerSubsidie = lazy(() => import("./pages/content/IsdeZonneboilerSubsidie"));
const IsdeIsolatieSubsidie = lazy(() => import("./pages/content/IsdeIsolatieSubsidie"));
const IsdeHrGlasSubsidie = lazy(() => import("./pages/content/IsdeHrGlasSubsidie"));
const IsdeVentilatieUnitSubsidie = lazy(() => import("./pages/content/IsdeVentilatieUnitSubsidie"));
const Bronnen = lazy(() => import("./pages/content/Bronnen"));
const RvoMeldcodelijstUitgelegd = lazy(() => import("./pages/content/RvoMeldcodelijstUitgelegd"));
const SubsidiebedragControleren = lazy(() => import("./pages/content/SubsidiebedragControleren"));
const MeldcodeOpOfferteControleren = lazy(() => import("./pages/content/MeldcodeOpOfferteControleren"));
const AdminSeoChecklist = lazy(() => import("./pages/AdminSeoChecklist"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/zoeken" element={<SearchPage />} />
            <Route path="/meldcode/:meldcode" element={<ProductPage />} />
            <Route path="/categorie/:slug" element={<CategoryPage />} />
            <Route path="/merk/:slug" element={<BrandPage />} />
            <Route path="/niet-gevonden" element={<MissingCodePage />} />
            <Route path="/datakwaliteit" element={<DataQuality />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/affiliate-disclosure" element={<Affiliate />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/monetisatie" element={<AdminMonetization />} />
            <Route path="/wat-is-een-isde-meldcode" element={<WatIsEenIsdeMeldcode />} />
            <Route path="/isde-meldcode-zoeken" element={<IsdeMeldcodeZoeken />} />
            <Route path="/waar-vind-ik-mijn-meldcode" element={<WaarVindIkMijnMeldcode />} />
            <Route path="/product-niet-in-meldcodelijst" element={<ProductNietInMeldcodelijst />} />
            <Route path="/isde-subsidie-aanvragen" element={<IsdeSubsidieAanvragen />} />
            <Route path="/isde-subsidie-2026" element={<IsdeSubsidie2026 />} />
            <Route path="/isde-subsidie-berekenen" element={<IsdeSubsidieBerekenen />} />
            <Route path="/isde-meldcodelijst-2026" element={<IsdeMeldcodelijst2026 />} />
            <Route path="/isde-warmtepomp-subsidie" element={<IsdeWarmtepompSubsidie />} />
            <Route path="/isde-zonneboiler-subsidie" element={<IsdeZonneboilerSubsidie />} />
            <Route path="/isde-isolatie-subsidie" element={<IsdeIsolatieSubsidie />} />
            <Route path="/isde-hr-glas-subsidie" element={<IsdeHrGlasSubsidie />} />
            <Route path="/isde-ventilatie-unit-subsidie" element={<IsdeVentilatieUnitSubsidie />} />
            <Route path="/bronnen" element={<Bronnen />} />
            <Route path="/rvo-meldcodelijst-uitgelegd" element={<RvoMeldcodelijstUitgelegd />} />
            <Route path="/subsidiebedrag-controleren" element={<SubsidiebedragControleren />} />
            <Route path="/meldcode-op-offerte-controleren" element={<MeldcodeOpOfferteControleren />} />
            <Route path="/admin/seo-checklist" element={<AdminSeoChecklist />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
